import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import DeleteModal from '../DeleteModal';

/* badge shown on the dropdown toggle for each terminal / intermediate status */
const STATUS_BADGES = {
  approve: { icon: 'mdi-check-circle text-success', label: 'Approved' },
  reject: { icon: 'mdi-close-circle text-danger', label: 'Rejected' },
  cancel: { icon: 'mdi-close-circle text-danger', label: 'Cancelled' },
  reviewed: { icon: 'mdi-check-circle text-warning', label: 'Reviewed' },
};

/* verb for the final audit-trail line, keyed by terminal status */
const FINAL_STATUS_VERBS = {
  approve: 'approved',
  reject: 'rejected',
  cancel: 'cancelled',
};

/* shown when an approver acts on a request that has not been reviewed yet */
const BYPASS_REVIEW_MODAL = {
  title: 'Bypass the review step?',
  content:
    'This request is still pending review. Approving it now will bypass the review step.',
  body: true,
};

const fullName = (person) =>
  `${person.firstName} ${person.lastName || ''}`.trim();

const WorkSessionApprovalToggle = ({
  status,
  wsDetails,
  wsRequestStatusUpdate,
  handleSelectedRequest,
  handleEditOnApprovalModal,
  isReviewEnabled,
  canReview,
  canApprove,
}) => {
  const [sessionProjectId] = useState(
    wsDetails.newProject && wsDetails.newProject !== null
      ? wsDetails.newProject._id
      : wsDetails.oldProject._id
  );

  /* Which action set the viewer gets for this row. Approval permission wins
     on a pending request: holding it means the review step may be bypassed,
     so someone with both permissions is treated as an approver. */
  let mode = 'readonly';
  if (status === 'pending') {
    if (canApprove) {
      mode = 'approve';
    } else if (isReviewEnabled && canReview) {
      mode = 'review';
    }
  } else if (status === 'reviewed') {
    mode = canApprove ? 'approve' : 'readonly';
  }

  /* approving a pending request while review is enabled skips a step the
     account has asked for, so it is confirmed first */
  const isBypassingReview =
    mode === 'approve' && status === 'pending' && isReviewEnabled;

  /* holds the queued action (and its typed remark) while the confirmation is
     open, since the dropdown's form unmounts as soon as the menu closes */
  const [pendingBypass, setPendingBypass] = useState(null);

  const runBypassAction = () => {
    if (!pendingBypass) {
      return;
    }
    const { action, remark } = pendingBypass;
    setPendingBypass(null);
    if (action === 'edit') {
      handleSelectedRequest({ ...wsDetails, remark });
      handleEditOnApprovalModal(true, sessionProjectId);
    } else {
      wsRequestStatusUpdate({
        workSessionId: wsDetails.workSessionId,
        requestId: wsDetails._id,
        status: 'approve',
        updateRemark: remark,
      });
    }
  };

  const badge = STATUS_BADGES[status] || {
    icon: 'mdi-check-circle text-warning',
    label: isReviewEnabled ? 'Pending for review' : 'Pending for approval',
  };

  /* one line per step the request has been through */
  const history = [
    {
      date: wsDetails.requestedDate,
      text: `${fullName(wsDetails.requestedBy)} requested a work session edit`,
    },
  ];
  if (wsDetails.reviewedBy) {
    history.push({
      date: wsDetails.reviewedDate,
      text: `${fullName(wsDetails.reviewedBy)} reviewed the request`,
      remark: wsDetails.reviewRemark,
    });
  }
  if (wsDetails.updatedBy && FINAL_STATUS_VERBS[status]) {
    history.push({
      date: wsDetails.updatedDate,
      text: `${fullName(wsDetails.updatedBy)} ${
        FINAL_STATUS_VERBS[status]
      } the request`,
      remark: wsDetails.updateRemark,
    });
  }

  const submitStatus = mode === 'review' ? 'review' : 'approve';

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          as={Button}
          size="sm"
          variant="white"
          className="mt-2"
          to={{ hash: '#' }}
        >
          <i className={`mdi ${badge.icon} me-1`} />
          {badge.label}
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="dropdown-menu-right p-3 mt-1"
          style={{ minWidth: '300px' }}
        >
          {mode !== 'readonly' && (
            <Formik
              initialValues={{
                remarks: '',
              }}
              onSubmit={(values, { resetForm }) => {
                if (isBypassingReview) {
                  setPendingBypass({
                    action: 'approve',
                    remark: values.remarks,
                  });
                  return;
                }
                wsRequestStatusUpdate({
                  workSessionId: wsDetails.workSessionId,
                  requestId: wsDetails._id,
                  status: submitStatus,
                  updateRemark: values.remarks,
                });
                resetForm();
              }}
            >
              {(formikprops) => (
                <Form onSubmit={formikprops.handleSubmit}>
                  <Form.Group controlId="wsNotes" className="mb-3">
                    <Field
                      as="textarea"
                      maxLength="300"
                      name="remarks"
                      rows="2"
                      className="form-control"
                      placeholder="Remarks"
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-start">
                    <Button
                      variant="primary"
                      type="submit"
                      size="sm"
                      className="me-2"
                    >
                      {mode === 'review' ? 'Mark as Reviewed' : 'Approve'}
                    </Button>
                    {mode === 'approve' && (
                      <Button
                        variant="white"
                        type="button"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          if (isBypassingReview) {
                            setPendingBypass({
                              action: 'edit',
                              remark: formikprops.values.remarks,
                            });
                            return;
                          }
                          handleSelectedRequest({
                            ...wsDetails,
                            remark: formikprops.values.remarks,
                          });
                          handleEditOnApprovalModal(true, sessionProjectId);
                        }}
                      >
                        Edit & Approve
                      </Button>
                    )}
                    <Button
                      variant="white"
                      type="button"
                      size="sm"
                      onClick={() => {
                        if (formikprops.values.remarks.length > 0) {
                          wsRequestStatusUpdate({
                            workSessionId: wsDetails.workSessionId,
                            requestId: wsDetails._id,
                            status: 'reject',
                            updateRemark: formikprops.values.remarks,
                          });
                        } else {
                          toast.error('Please enter rejection remarks');
                        }
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
          <ul
            className={`list-unstyled ${mode === 'readonly' ? 'mb-0' : 'mt-4'}`}
          >
            {history.map((item) => (
              <li key={item.text}>
                <small>
                  <span className="text-muted">
                    {moment(item.date).format('MMM D, hh:mm A')}{' '}
                  </span>
                  {item.text}
                  {item.remark && (
                    <span>
                      , adding the remark: <em>{item.remark}</em>
                    </span>
                  )}
                </small>
              </li>
            ))}
          </ul>
        </Dropdown.Menu>
      </Dropdown>
      <DeleteModal
        modalProps={BYPASS_REVIEW_MODAL}
        showModal={Boolean(pendingBypass)}
        handleDelete={runBypassAction}
        handleHideModal={() => setPendingBypass(null)}
      />
    </>
  );
};

WorkSessionApprovalToggle.defaultProps = {
  isReviewEnabled: false,
  canReview: false,
  canApprove: true,
};

WorkSessionApprovalToggle.propTypes = {
  status: PropTypes.string,
  wsDetails: PropTypes.object,
  wsRequestStatusUpdate: PropTypes.func,
  handleSelectedRequest: PropTypes.func,
  handleEditOnApprovalModal: PropTypes.func,
  isReviewEnabled: PropTypes.bool,
  canReview: PropTypes.bool,
  canApprove: PropTypes.bool,
};

export default WorkSessionApprovalToggle;
